/* eslint-disable @typescript-eslint/no-use-before-define */
import React, { useMemo, useRef, useState } from 'react'
import { Group } from '@visx/group'
import { AxisBottom, AxisLeft } from '@visx/axis'
import { scaleLinear, scaleTime } from '@visx/scale'
import { LinePath } from '@visx/shape'
import { extent, max, bisector } from '@visx/vendor/d3-array'
import { Brush } from '@visx/brush'
import type { Bounds } from '@visx/brush/lib/types'
import type BaseBrush from '@visx/brush/lib/BaseBrush'
import { useTooltip, TooltipWithBounds } from '@visx/tooltip'
import { localPoint } from '@visx/event'
import { LinearGradient } from '@visx/gradient'
import { format } from 'date-fns'
import { Drag, raise } from '@visx/drag';
import type { Series } from './MiniDepthChart'
import type { GeoJsonFeature } from '../../utils/ogc'



type HoverProps = {
  series: Series[]
  children?: React.ReactNode
  xScale: ReturnType<typeof scaleTime<number>>
  yScale: ReturnType<typeof scaleLinear<number>>
  width: number
  height: number
  highlightedKey?: string
  colorFor?: (i: number, key?: string) => string
  offsetLeft?: number
  offsetTop?: number
  onClick?: (feature: GeoJsonFeature) => void
  onHoverSeriesChange?: (key: string | null) => void
  onSeriesToggle?: (key: string) => void
  onMouseDownCapture?: (e: React.MouseEvent<SVGRectElement>) => void
  onMouseMoveCapture?: (e: React.MouseEvent<SVGRectElement>) => void
  onMouseUpCapture?: (e: React.MouseEvent<SVGRectElement>) => void
  cancelClickRef?: React.MutableRefObject<boolean | undefined>
}

export default function HoverOverlay({ series, children, xScale, yScale, width, height, colorFor, offsetLeft = 0, offsetTop = 0, onClick, onHoverSeriesChange, onMouseDownCapture, onMouseMoveCapture, onMouseUpCapture, cancelClickRef }: HoverProps) {
  const [hoveredFeature, setHoveredFeature] = useState<GeoJsonFeature | null>(null);
  const { showTooltip, hideTooltip, tooltipData, tooltipLeft, tooltipTop } = useTooltip<{ key: string; p: Point; color: string, label: string }>()
  const onLeave = () => { hideTooltip(); onHoverSeriesChange?.(null); setHoveredFeature(null); }
  const onMove = (e: React.MouseEvent<SVGRectElement>) => {
    const pt = localPoint(e)
    if (!pt) return
    const localX = Math.max(0, Math.min(width, pt.x - offsetLeft))
    const localY = Math.max(0, Math.min(height, pt.y - offsetTop))
    const x0 = xScale.invert(localX)

    let best: { key: string; label: string; p: Point; color: string; i: number; dist2: number, feature: GeoJsonFeature, dx: number, } | null = null
    const bisect = bisector<Point, Date>((p) => p.t).left
    series.forEach((s, i) => {
      const idx = bisect(s.data, x0)
      const candidates = [s.data[idx - 1], s.data[idx]].filter(Boolean) as Point[]
      for (const p of candidates) {
        const px = xScale(p.t)
        const py = yScale(p.d)
        const dx = px - localX
        const dy = py - localY
        const dist2 = dx * dx + dy * dy
        if (best == null || dist2 < best.dist2) best = { key: s.key, label: s.label, p, color: '', i, dist2, dx: Math.abs(dx), feature: s.feature }
      }
    })
    if (!best) return
    if (best.dx > 30) { onLeave(); return; } // ignore if too far horizontally
    best.color = colorFor?.(best.i, best.key) || 'blue';
    showTooltip({ 
      tooltipData: { 
        key: best.key, 
        label: best.label,
        p: best.p, 
        color: colorFor?.(best.i, best.key) || 'blue',
      }, 
      tooltipLeft: xScale(best.p.t), 
      tooltipTop: yScale(best.p.d) 
    })
    onHoverSeriesChange?.(best.key)
    setHoveredFeature(best.feature);
  }
  // const [hoveredFeature, setHoveredFeature] = useState<GeoJsonFeature | null>(null);
  // const { showTooltip, hideTooltip, tooltipData, tooltipLeft, tooltipTop } = useTooltip<{ key: string; p: Point; color: string }>()
  // const onLeave = () => { hideTooltip(); onHoverSeriesChange?.(null); setHoveredFeature(null); }
  // const onMove = (e: React.MouseEvent<SVGRectElement>) => {
  //   const pt = localPoint(e)
  //   if (!pt) return
  //   const localX = Math.max(0, Math.min(width, pt.x - offsetLeft))
  //   const localY = Math.max(0, Math.min(height, pt.y - offsetTop))
  //   const x0 = xScale.invert(localX)
  //   let best: { key: string; p: Point; color: string; i: number; dist2: number, feature: GeoJsonFeature, dx: number } | null = null
  //   const bisect = bisector<Point, Date>((p) => p.t).left
  //   series.forEach((s, i) => {
  //     const idx = bisect(s.data, x0)
  //     const candidates = [s.data[idx - 1], s.data[idx]].filter(Boolean) as Point[]
  //     for (const p of candidates) {
  //       const px = xScale(p.t)
  //       const py = yScale(p.d)
  //       const dx = px - localX
  //       const dy = py - localY
  //       const dist2 = dx * dx + dy * dy
  //       if (best == null || dist2 < best.dist2) best = { key: s.key, p, color: '', i, dist2, dx: Math.abs(dx), feature: s.feature }
  //     }
  //   })
  //   if (!best) return
  //   if (best.dx > 30) { onLeave(); return; } // ignore if too far horizontally
  //   best.color = colorFor?.(best.i, best.key) || 'blue';
  //   showTooltip({ tooltipData: { key: best.key, p: best.p, color: best.color }, tooltipLeft: xScale(best.p.t), tooltipTop: yScale(best.p.d) })
  //   onHoverSeriesChange?.(best.key)
  //   setHoveredFeature(best.feature);
  // }

  const tooltipWidth = 180
  const tooltipOffset = 8
  const tooltipX = (() => {
    const baseLeft = tooltipLeft ?? 0
    const alignRight = baseLeft > width - tooltipWidth
    return alignRight ? Math.max(0, baseLeft - tooltipWidth - tooltipOffset) : baseLeft + tooltipOffset
  })()
  const tooltipY = (tooltipTop ?? 0) - 24
  return (
    <g  >
      {children}
      <rect
        width={width}
        height={height}
        fill="transparent"
        style={{ cursor: 'pointer' }}
        onMouseDownCapture={onMouseDownCapture}
        onMouseMoveCapture={onMouseMoveCapture}
        onMouseUpCapture={onMouseUpCapture}
        onMouseMove={onMove}
        onMouseLeave={onLeave}
        onClick={() => {
          if (cancelClickRef?.current) { cancelClickRef.current = false; return; }
          hoveredFeature && onClick?.(hoveredFeature)
        }}
      />
      {tooltipData && (
        <g pointerEvents="none">
          <line x1={tooltipLeft} x2={tooltipLeft} y1={0} y2={height} stroke="#999" strokeDasharray="3,3" pointerEvents="none" />
          <circle cx={tooltipLeft} cy={tooltipTop} r={3} fill={tooltipData.color} stroke="#fff" pointerEvents="none" />
          <foreignObject x={tooltipX} y={tooltipY} width={tooltipWidth} height={100} pointerEvents={"none"}>
            <div style={{ background: 'rgba(0,0,0,0.75)', color: 'white', padding: '4px 6px', borderRadius: 4, fontSize: 12, pointerEvents: 'none', whiteSpace: 'nowrap' }}>
              <div style={{ fontWeight: 600 }}>{tooltipData.label || tooltipData.key}</div>
              {/* tooltipData.p.t.toLocaleString() */}
              <div>{tooltipData.p.d.toFixed(1)}" · {tooltipData.p.label ? ` ${tooltipData.p.label}` : ' unknown'}</div>
              <div>{format(tooltipData.p.t, 'eee h:mm a')}</div>
            </div>
          </foreignObject>
        </g>
      )}
      
    </g>
  )
}
